import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Paper,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  CircularProgress,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import LockIcon from '@material-ui/icons/Lock'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import PriorityHighIcon from '@material-ui/icons/PriorityHigh'
import useChatbotStore from 'src/components/chatbot/stores/chatbotStore'
import useMultiSampleOptions from 'src/hooks/useMultiSampleOptions'

const predefinedRelations = [
  'Proband',
  'Mother',
  'Father',
  'Sibling',
  'Grandparent',
  'Aunt',
  'Uncle',
  'Cousin',
  'Son',
  'Daughter',
  'Other',
]

const LOCAL_STORAGE_KEY = 'multiSampleAnalysisRelations'

// ---------------------------------------------------------------------------
// ChatbotSampleAutocomplete – each instance owns its own search/options state
// ---------------------------------------------------------------------------
function ChatbotSampleAutocomplete({ fileId, selectedOption, onChange, disabledFileIds, error, helperText }) {
  const { options, isSearching, setSearchTerm } = useMultiSampleOptions()
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  })

  // Auto-resolve fileId → option when options load (for restoring saved state)
  useEffect(() => {
    if (fileId && !selectedOption) {
      const found = options.find((o) => o.id === fileId)
      if (found) onChangeRef.current(found)
    }
  }, [fileId, selectedOption, options])

  // Always include the selected option in the dropdown
  const displayOptions = useMemo(() => {
    if (!selectedOption || options.some((o) => o.id === selectedOption.id)) return options
    return [selectedOption, ...options]
  }, [options, selectedOption])

  return (
    <Autocomplete
      options={displayOptions}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      value={selectedOption}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={(_, value, reason) => {
        if (reason === 'input') setSearchTerm(value)
      }}
      onClose={() => setSearchTerm('')}
      loading={isSearching}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Sample"
          placeholder="Search samples..."
          error={error}
          helperText={helperText}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isSearching ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          style={{
            opacity: !option.completed || disabledFileIds.includes(option.id) ? 0.5 : 1,
          }}
        >
          {option.label}
          {!option.completed && (
            <Tooltip title="Analysis not completed" arrow>
              <LockIcon style={{ marginLeft: 8, fontSize: 16 }} />
            </Tooltip>
          )}
        </li>
      )}
      getOptionDisabled={(option) => !option.completed || disabledFileIds.includes(option.id)}
      noOptionsText={isSearching ? 'Searching...' : 'No samples found'}
    />
  )
}

// ---------------------------------------------------------------------------
// MultiSampleAnalysisModal – chatbot modal component
// ---------------------------------------------------------------------------
const MultiSampleAnalysisModal = function ({ value = [], onChange, onClose }) {
  const { fileId, sampleName } = useParams()
  const { idToName } = useChatbotStore()
  const [relatedSamples, setRelatedSamples] = useState([])
  const [touchedFields, setTouchedFields] = useState({})
  const [attemptedApply, setAttemptedApply] = useState(false)
  const [isValid, setIsValid] = useState(true)

  // Initialize on mount from value prop + localStorage
  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || {}

    if (value && value.length > 0) {
      const samples = value.map((sample, index) => {
        const val = storedData[sample.sample_id]
        const relation =
          (typeof val === 'string' ? val : val?.relation) || sample.relation || (index === 0 ? 'Proband' : '')
        const storedName = typeof val === 'object' ? val?.sampleName : ''

        return {
          include: sample.include,
          fileId: sample.sample_id,
          relation,
          gt: sample.genotype || 'Any',
          disabled: false,
          selectedOption: storedName ? { id: sample.sample_id, label: storedName, tags: [], completed: true } : null,
          sampleName: storedName || '',
        }
      })
      setRelatedSamples(samples)
    } else {
      setRelatedSamples([
        {
          include: true,
          fileId: fileId,
          relation: 'Proband',
          gt: 'Any',
          disabled: true,
          selectedOption: null,
          sampleName: sampleName || '',
        },
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const disabledFileIds = useMemo(() => relatedSamples.map((s) => s.fileId).filter(Boolean), [relatedSamples])

  const handleAddRelationSample = () => {
    setRelatedSamples((prev) => [
      ...prev,
      { include: true, fileId: '', relation: '', gt: 'Any', disabled: false, selectedOption: null, sampleName: '' },
    ])
  }

  const handleDeleteRelationSample = (index) => {
    setRelatedSamples((prev) => prev.filter((_, i) => i !== index))
  }

  const handleChange = (index, field, value) => {
    setRelatedSamples((prev) => prev.map((sample, i) => (i === index ? { ...sample, [field]: value } : sample)))
    setTouchedFields((prev) => ({ ...prev, [index]: { ...prev[index], [field]: true } }))
  }

  const handleSampleSelect = (index, option) => {
    setRelatedSamples((prev) =>
      prev.map((sample, i) =>
        i === index
          ? { ...sample, fileId: option?.id || '', selectedOption: option, sampleName: option?.label || '' }
          : sample,
      ),
    )
    setTouchedFields((prev) => ({ ...prev, [index]: { ...prev[index], fileId: true } }))
  }

  const handleSave = () => {
    setAttemptedApply(true)

    const isValidForm = relatedSamples.every((sample, index) => index === 0 || (sample.fileId && sample.relation))
    if (!isValidForm) {
      setIsValid(false)
      return
    }

    // Save relations + sample names to localStorage (new format)
    const stored = relatedSamples.reduce((acc, { fileId: id, relation, sampleName: name }) => {
      if (id) acc[id] = { relation, sampleName: name || '' }
      return acc
    }, {})
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(stored))

    // Prepare the data for the backend
    const backendSamples = relatedSamples.map((sample) => ({
      sample_id: sample.fileId,
      relation: sample.relation,
      genotype: sample.gt,
      include: sample.include,
    }))

    onChange(backendSamples)
    onClose()
  }

  const showError = (index, field) => {
    return (attemptedApply || touchedFields[index]?.[field]) && !relatedSamples[index][field]
  }

  useEffect(() => {
    const isValidForm = relatedSamples.every((sample, index) => index === 0 || (sample.fileId && sample.relation))
    setIsValid(isValidForm)
  }, [relatedSamples])

  return (
    <Box style={{ width: '100%', maxWidth: 500, margin: '0 auto', padding: 16 }}>
      <Typography variant="h6" gutterBottom color="primary">
        Multi-Sample Analysis for {sampleName}
      </Typography>
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {relatedSamples.map((sample, index) => (
          <Paper key={index} elevation={2} style={{ padding: 12 }}>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2">
                  {index === 0 ? 'Original Sample' : `Related Sample ${index}`}
                </Typography>
                <ToggleButtonGroup
                  value={sample.include}
                  exclusive
                  onChange={(_, value) => {
                    if (value != null && value !== sample.include) {
                      handleChange(index, 'include', value)
                    }
                  }}
                  disabled={false}
                  size="small"
                >
                  <ToggleButton value={true} color="primary">
                    <Tooltip title="Include variants of this sample" arrow>
                      <CheckCircleIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value={false} color="secondary">
                    <Tooltip title="Exclude variants of this sample" arrow>
                      <CancelIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <FormControl fullWidth size="small">
                {index === 0 ? (
                  <TextField value={sampleName} label="Sample" disabled size="small" fullWidth />
                ) : (
                  <ChatbotSampleAutocomplete
                    fileId={sample.fileId}
                    selectedOption={sample.selectedOption || null}
                    onChange={(option) => handleSampleSelect(index, option)}
                    disabledFileIds={disabledFileIds}
                    error={showError(index, 'fileId')}
                    helperText={showError(index, 'fileId') ? 'Sample is required' : ''}
                  />
                )}
              </FormControl>
              <Autocomplete
                freeSolo
                options={predefinedRelations}
                value={sample.relation}
                onChange={(_, newValue) => handleChange(index, 'relation', newValue)}
                onInputChange={(_, newValue) => handleChange(index, 'relation', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Relation"
                    size="small"
                    error={showError(index, 'relation')}
                    helperText={showError(index, 'relation') ? 'Relation is required' : ''}
                  />
                )}
                disabled={false}
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Genotype</InputLabel>
                <Select
                  value={sample.gt}
                  onChange={(event) => handleChange(index, 'gt', event.target.value)}
                  label="Genotype"
                  disabled={false}
                >
                  <MenuItem value="Any">Any</MenuItem>
                  <MenuItem value="Homozygous">Homozygous</MenuItem>
                  <MenuItem value="Heterozygous">Heterozygous</MenuItem>
                </Select>
              </FormControl>
              {index !== 0 && (
                <IconButton
                  onClick={() => handleDeleteRelationSample(index)}
                  color="error"
                  size="small"
                  style={{ alignSelf: 'flex-end' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Paper>
        ))}
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddRelationSample} fullWidth size="small">
          Add New Relation
        </Button>
        <Box style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
          {(relatedSamples.length <= 1 || !relatedSamples.some((s) => s.include)) && (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: '0.75rem',
                marginTop: 16,
                textAlign: 'center',
              }}
              sx={{
                color: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.warning.dark : theme.palette.warning.main,
              }}
            >
              <PriorityHighIcon style={{ fontSize: '16px' }} />
              <Typography variant="caption">
                {relatedSamples.length <= 1
                  ? 'Please select multiple samples for multi sample analysis.'
                  : 'Please include multiple samples for multi sample analysis.'}
              </Typography>
            </Box>
          )}
        </Box>
        <Box style={{ display: 'flex', flexDirection: 'row', gap: 16, marginTop: 16 }}>
          <Button variant="outlined" color="secondary" onClick={onClose} fullWidth size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isValid || relatedSamples.length <= 1 || !relatedSamples.some((s) => s.include)}
            fullWidth
            size="small"
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default MultiSampleAnalysisModal
